import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Sparkles, Target, Clock, Calendar, BookMarked, Flame, 
  ChevronRight, ChevronLeft, Brain, TrendingUp, Lightbulb,
  CheckCircle2, AlertCircle, Zap, BookOpen
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, addDays, startOfWeek, isSameDay } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Priority Badge Component
const PriorityBadge = ({ priority }) => {
  const colors = {
    high: 'bg-red-500/20 text-red-400 border-red-500/30',
    medium: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    low: 'bg-green-500/20 text-green-400 border-green-500/30',
    critical: 'bg-purple-500/20 text-purple-400 border-purple-500/30'
  };
  
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${colors[priority] || colors.medium}`}>
      {priority}
    </span>
  );
};

// Recommendation Card Component
const RecommendationCard = ({ recommendation, index }) => {
  const getIcon = (type) => {
    const icons = {
      'optimal_time': Clock,
      'weak_area': Target,
      'exam_prep': Calendar,
      'spaced_repetition': BookMarked,
      'streak_bonus': Flame
    };
    const Icon = icons[type] || Lightbulb;
    return <Icon className="w-5 h-5" />;
  };
  
  const getIconColor = (type) => {
    const colors = {
      'optimal_time': 'from-blue-500 to-cyan-500',
      'weak_area': 'from-red-500 to-orange-500',
      'exam_prep': 'from-amber-500 to-yellow-500',
      'spaced_repetition': 'from-purple-500 to-pink-500',
      'streak_bonus': 'from-orange-500 to-red-500'
    };
    return colors[type] || 'from-cyan-500 to-blue-500';
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
    >
      <Card className="p-4  bg-[var(--isp-glass)] backdrop-blur-xl border-[var(--isp-glass-border)] hover:border-white/20 transition-all">
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-xl bg-gradient-to-br ${getIconColor(recommendation.type)} flex-shrink-0`}>
            {getIcon(recommendation.type)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 mb-1">
              <h3 className="text-[var(--isp-heading)] font-semibold truncate">{recommendation.title}</h3>
              <PriorityBadge priority={recommendation.priority} />
            </div>
            <p className="text-[var(--isp-body)] text-sm mb-3">{recommendation.description}</p>
            {recommendation.subject_name && (
              <span className="inline-flex items-center gap-1 text-xs text-purple-400 bg-purple-500/10 px-2 py-1 rounded-lg">
                <BookOpen className="w-3 h-3" />
                {recommendation.subject_name}
              </span>
            )}
          </div>
          {recommendation.action_url && (
            <Button size="sm" variant="ghost" className="text-[var(--isp-body)] hover:text-[var(--isp-heading)]" asChild>
              <a href={recommendation.action_url}>
                <ChevronRight className="w-5 h-5" />
              </a>
            </Button>
          )}
        </div>
      </Card>
    </motion.div>
  );
};

// Weekly Calendar Component
const WeeklyCalendar = ({ schedule, selectedDate, onSelectDate }) => {
  const weekStart = startOfWeek(new Date());
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  
  return (
    <div className="grid grid-cols-7 gap-2 mb-6">
      {days.map((day, index) => {
        const daySchedule = schedule?.find(s => s.date === format(day, 'yyyy-MM-dd'));
        const isSelected = selectedDate && isSameDay(day, selectedDate);
        const hasExam = daySchedule?.sessions?.some(s => s.type === 'exam');
        const hasSessions = daySchedule?.sessions?.length > 0;
        
        return (
          <motion.button
            key={index}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.05 }}
            onClick={() => onSelectDate(day)}
            className={`p-3 rounded-xl text-center transition-all ${
              isSelected 
                ? 'bg-gradient-to-br from-purple-500 to-indigo-600 text-white' 
                : 'bg-[var(--isp-glass)] backdrop-blur-xl hover:bg-[var(--isp-glass-inner)] text-[var(--isp-body)]'
            } ${hasExam ? 'ring-2 ring-amber-500/50' : ''}`}
          >
            <div className="text-xs opacity-60 mb-1">{format(day, 'EEE')}</div>
            <div className="text-lg font-bold">{format(day, 'd')}</div>
            {hasSessions && (
              <div className="flex justify-center gap-1 mt-1">
                {daySchedule.sessions.slice(0, 3).map((_, i) => (
                  <div 
                    key={`item-${i}`} 
                    className={`w-1.5 h-1.5 rounded-full ${
                      hasExam ? 'bg-amber-400' : 'bg-purple-400'
                    }`} 
                  />
                ))}
              </div>
            )}
          </motion.button>
        );
      })}
    </div>
  );
};

// Schedule Session Card
const SessionCard = ({ session, index }) => {
  const getTypeIcon = (type) => {
    return type === 'exam' ? Calendar : BookOpen;
  };
  
  const Icon = getTypeIcon(session.type);
  
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.1 }}
      className={`flex items-center gap-4 p-4 rounded-xl ${
        session.type === 'exam' 
          ? 'bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30'
          : session.priority === 'high'
            ? 'bg-gradient-to-r from-red-500/10 to-rose-500/10 border border-red-500/20'
            : 'bg-slate-800/30 border border-[var(--isp-glass-border)]'
      }`}
    >
      <div className={`p-2 rounded-lg ${
        session.type === 'exam' ? 'bg-amber-500/20 text-amber-400' : 'bg-purple-500/20 text-purple-400'
      }`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="text-[var(--isp-heading)] font-medium">{session.time}</span>
          {session.duration_minutes && (
            <span className="text-[var(--isp-muted)] text-sm">({session.duration_minutes} min)</span>
          )}
        </div>
        <p className="text-[var(--isp-body)] text-sm">{session.title}</p>
      </div>
      <PriorityBadge priority={session.priority} />
    </motion.div>
  );
};

// Insight Card Component
const InsightCard = ({ insight }) => {
  const getTrendIcon = (trend) => {
    if (trend === 'up') return <TrendingUp className="w-4 h-4 text-green-400" />;
    if (trend === 'down') return <TrendingUp className="w-4 h-4 text-red-400 rotate-180" />;
    return <div className="w-4 h-4 bg-white/20 rounded-full" />;
  };
  
  return (
    <Card className="p-4 rounded-xl bg-slate-800/30 border-[var(--isp-glass-border)]">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[var(--isp-body)] text-sm">{insight.details}</span>
        {getTrendIcon(insight.trend)}
      </div>
      <div className="text-2xl font-bold text-white">{insight.value}</div>
    </Card>
  );
};

// Main Recommendations Page
const RecommendationsPage = () => {
  const [recommendations, setRecommendations] = useState([]);
  const [insights, setInsights] = useState([]);
  const [summary, setSummary] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('recommendations');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [recsRes, scheduleRes] = await Promise.all([
        axios.get(`${API}/recommendations/dashboard`),
        axios.get(`${API}/recommendations/study-schedule?days=7`)
      ]);
      
      setRecommendations(recsRes.data.recommendations || []);
      setInsights(recsRes.data.insights || []);
      setSummary(recsRes.data.summary || {});
      setSchedule(scheduleRes.data.schedule || []);
    } catch (error) {
      console.error('Error fetching recommendations:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectedDaySchedule = schedule.find(
    s => s.date === format(selectedDate, 'yyyy-MM-dd')
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20" data-testid="recommendations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
            <Sparkles className="w-8 h-8 text-purple-400" />
            AI Study Assistant
          </h1>
          <p className="text-[var(--isp-body)] mt-1">Personalized recommendations powered by AI</p>
        </div>
        
        {summary?.study_streak > 0 && (
          <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-orange-500/20 to-red-500/20 border border-orange-500/30">
            <Flame className="w-5 h-5 text-orange-400" />
            <span className="text-[var(--isp-heading)] font-semibold">{summary.study_streak}-Day Streak!</span>
          </div>
        )}
      </div>

      {/* Insights Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {insights.map((insight, index) => (
          <InsightCard key={insight.id || `insight-${index}`} insight={insight} />
        ))}
        {summary && (
          <>
            <Card className="p-4 rounded-xl bg-gradient-to-br from-purple-500/20 to-indigo-500/20 border-purple-500/30">
              <div className="text-[var(--isp-body)] text-sm mb-2">High Priority</div>
              <div className="text-2xl font-bold text-white">{summary.high_priority || 0}</div>
            </Card>
            <Card className="p-4 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 border-amber-500/30">
              <div className="text-[var(--isp-body)] text-sm mb-2">Due Reviews</div>
              <div className="text-2xl font-bold text-white">{summary.due_reviews || 0}</div>
            </Card>
          </>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 p-1 bg-[var(--isp-glass)] backdrop-blur-xl rounded-xl w-fit">
        {['recommendations', 'schedule'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab
                ? 'bg-purple-500 text-white'
                : 'text-[var(--isp-body)] hover:text-[var(--isp-heading)] hover:bg-[var(--isp-glass-inner)]'
            }`}
          >
            {tab === 'recommendations' ? 'Recommendations' : 'Study Schedule'}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {activeTab === 'recommendations' ? (
          <motion.div
            key="recommendations"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-4"
          >
            {recommendations.length > 0 ? (
              recommendations.map((rec, index) => (
                <RecommendationCard key={rec.id || `rec-${index}`} recommendation={rec} index={index} />
              ))
            ) : (
              <Card className="p-8  bg-[var(--isp-glass)] backdrop-blur-xl border-[var(--isp-glass-border)] text-center">
                <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-4" />
                <h3 className="text-[var(--isp-heading)] font-semibold text-lg mb-2">You're All Caught Up!</h3>
                <p className="text-[var(--isp-body)]">
                  No recommendations right now. Keep studying to generate personalized insights!
                </p>
              </Card>
            )}
          </motion.div>
        ) : (
          <motion.div
            key="schedule"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            {/* Weekly Calendar */}
            <Card className="p-4  bg-[var(--isp-glass)] backdrop-blur-xl border-[var(--isp-glass-border)] mb-6">
              <h3 className="text-[var(--isp-heading)] font-semibold mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-purple-400" />
                This Week
              </h3>
              <WeeklyCalendar 
                schedule={schedule} 
                selectedDate={selectedDate}
                onSelectDate={setSelectedDate}
              />
            </Card>

            {/* Selected Day Schedule */}
            <Card className="p-4  bg-[var(--isp-glass)] backdrop-blur-xl border-[var(--isp-glass-border)]">
              <h3 className="text-[var(--isp-heading)] font-semibold mb-4">
                {format(selectedDate, 'EEEE, MMMM d')}
              </h3>
              
              {selectedDaySchedule?.sessions?.length > 0 ? (
                <div className="space-y-3">
                  {selectedDaySchedule.sessions.map((session, index) => (
                    <SessionCard key={session.id || `session-${index}`} session={session} index={index} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-[var(--isp-body)]">
                  <Calendar className="w-10 h-10 mx-auto mb-3 opacity-50" />
                  <p>No sessions scheduled for this day</p>
                </div>
              )}
            </Card>

            {/* Study Tips */}
            <Card className="p-4  bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border-cyan-500/20 mt-6">
              <h3 className="text-[var(--isp-heading)] font-semibold mb-3 flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-cyan-400" />
                Study Tips
              </h3>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-[var(--isp-body)] text-sm">
                  <Zap className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                  Take 5-10 minute breaks between study sessions
                </li>
                <li className="flex items-start gap-2 text-[var(--isp-body)] text-sm">
                  <Zap className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                  Review flashcards daily for best retention
                </li>
                <li className="flex items-start gap-2 text-[var(--isp-body)] text-sm">
                  <Zap className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                  Focus on weaker subjects before exams
                </li>
              </ul>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RecommendationsPage;
